/**
 * Lote de encontros emitidos pelo servidor (Fase A).
 * Cliente só anima; espécie/shiny/raridade vêm do RPC request_encounters.
 */
import { requestAltarEncountersRpc, requestEncountersRpc } from './collection-api.js';

const BATCH_SIZE = 30;
const QUEUE_VERSION = 1;
/**
 * Encontro só vale enquanto report_session aceita o encounter_id. Ficamos bem
 * abaixo da janela do servidor: um encontro guardado além disso vira abate
 * rejeitado — XP e loot perdidos, e o jogador ainda levava anomaly_flag.
 */
const ENCOUNTER_TTL_MS = 55 * 60 * 1000;
const MIN_QUEUE_REFILL = 10;

function normalizeSpecies(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized || null;
}

function normalizeContext({ route, routeType, species } = {}) {
  return {
    route: Math.max(1, Math.min(500, Math.trunc(Number(route) || 1))),
    routeType: String(routeType || 'normal').trim().toLowerCase() || 'normal',
    species: normalizeSpecies(species),
  };
}

function contextKey(context) {
  const normalized = normalizeContext(context);
  return `${normalized.route}|${normalized.routeType}|${normalized.species || ''}`;
}

/** Fail closed: só deixa entrar na fila o que pertence à requisição pedida. */
function encounterMatchesContext(row, context) {
  const normalized = normalizeContext(context);
  return !!row?.id
    && Number(row.route) === normalized.route
    && String(row.route_type || '').trim().toLowerCase() === normalized.routeType
    && (!normalized.species || normalizeSpecies(row.species) === normalized.species);
}

function encounterIssuedAt(row) {
  const parsed = Date.parse(row?.issued_at || '');
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Descarta o que o servidor não creditaria mais. Sem issued_at, mantém. */
function pruneStale(rows, now = Date.now()) {
  return (rows || []).filter((row) => {
    const issuedAt = encounterIssuedAt(row);
    return !issuedAt || now - issuedAt < ENCOUNTER_TTL_MS;
  });
}

let userId = '';
let enabled = false;
let available = null; // null=unknown, true/false after probe
let queue = [];
let batchId = null;
let lastRoute = 1;
let lastRouteType = 'normal';
let lastSpecies = null;
let activeAltarInvocation = null;
let queueGeneration = 0;
const inFlight = new Map();
let desiredContextKey = null;
let hooks = {
  getRouteLevel: () => 1,
  getRouteType: () => 'normal',
  getHuntSpecies: () => null,
  onAltarProgress: () => {},
};

const storageKey = () => `idlemon-encounter-sync:v${QUEUE_VERSION}:${userId}`;

function persist() {
  if (!userId) return;
  localStorage.setItem(storageKey(), JSON.stringify({
    version: QUEUE_VERSION,
    queue,
    batchId,
    lastRoute,
    lastRouteType,
    lastSpecies,
  }));
}

function restore() {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey()) || '{}');
    queue = Array.isArray(saved.queue)
      ? pruneStale(saved.queue.filter((e) => e?.id && e.state === 'issued'))
      : [];
    batchId = saved.batchId || null;
    lastRoute = Number(saved.lastRoute) || 1;
    lastRouteType = String(saved.lastRouteType || 'normal').trim().toLowerCase() || 'normal';
    lastSpecies = normalizeSpecies(saved.lastSpecies);
    desiredContextKey = contextKey({ route: lastRoute, routeType: lastRouteType, species: lastSpecies });
  } catch {
    queue = [];
    batchId = null;
    desiredContextKey = null;
  }
}

function currentContext(overrides = {}) {
  return normalizeContext({
    route: overrides.route ?? hooks.getRouteLevel(),
    routeType: overrides.routeType ?? hooks.getRouteType(),
    species: overrides.species !== undefined ? overrides.species : hooks.getHuntSpecies?.(),
  });
}

/** Muda o contexto aceito pela fila e invalida o lote anterior imediatamente. */
function adoptQueueContext(context) {
  const normalized = normalizeContext(context);
  const key = contextKey(normalized);
  const previousKey = contextKey({ route: lastRoute, routeType: lastRouteType, species: lastSpecies });
  desiredContextKey = key;
  if (previousKey === key) return normalized;
  queueGeneration += 1;
  queue = [];
  batchId = null;
  lastRoute = normalized.route;
  lastRouteType = normalized.routeType;
  lastSpecies = normalized.species;
  persist();
  return normalized;
}

export function encountersSyncEnabled() {
  return !!userId && enabled && available !== false;
}

export function setEncountersSyncEnabled(value) {
  enabled = !!value;
}

/**
 * Switches the next species-pinned batch to the server-authoritative altar RPC.
 * A normal queue is discarded so a previously issued non-altar encounter cannot
 * silently consume the altar route window.
 */
export function setShinyAltarInvocation(invocation = null) {
  if (!invocation?.species) {
    activeAltarInvocation = null;
    queueGeneration += 1;
    return;
  }
  activeAltarInvocation = {
    id: invocation.id || invocation.invocation_id || null,
    species: normalizeSpecies(invocation.species),
    remaining: Math.max(0, Number(invocation.remaining ?? invocation.encounters_remaining ?? 0) || 0),
  };
  queueGeneration += 1;
  queue = [];
  batchId = null;
  lastSpecies = activeAltarInvocation.species;
  desiredContextKey = contextKey({ route: lastRoute, routeType: lastRouteType, species: lastSpecies });
  persist();
}

export function clearShinyAltarInvocation() {
  if (activeAltarInvocation) queueGeneration += 1;
  activeAltarInvocation = null;
}

export async function probeEncountersAvailable() {
  if (!userId) {
    available = false;
    return false;
  }
  if (available != null) return available;
  try {
    const response = await requestEncountersRpc({
      route: Math.max(1, Math.min(500, Math.trunc(Number(hooks.getRouteLevel()) || 1))),
      count: 1,
      routeType: String(hooks.getRouteType() || 'normal').toLowerCase(),
      species: hooks.getHuntSpecies?.() || undefined,
    }, crypto.randomUUID());
    // O probe só verifica se o RPC existe. Nunca coloca seu encounter na fila:
    // ele pode ser sem pin e, portanto, pertencer a qualquer espécie.
    available = Array.isArray(response?.encounters);
  } catch (error) {
    const msg = String(error?.message || error?.code || '');
    if (/function|not find|PGRST|404|42883/i.test(msg)) available = false;
    else available = true;
  }
  return available !== false;
}

/** Consome sem await — use após ensureEncounterQueue. */
export function takeQueuedEncounterSync(opts = {}) {
  if (!encountersSyncEnabled()) return null;
  const context = currentContext(opts);
  adoptQueueContext(context);
  const before = queue.length;
  queue = pruneStale(queue).filter((row) => encounterMatchesContext(row, context));
  if (queue.length !== before) persist();
  if (!queue.length) return null;
  const next = queue.shift();
  persist();
  if (queue.length < MIN_QUEUE_REFILL) {
    // Refill em background — nunca await no tick/rAF
    fetchEncounterBatch({
      route: context.route,
      routeType: context.routeType,
      species: context.species,
      count: BATCH_SIZE,
      force: true,
    }).catch(() => {});
  }
  return next;
}

export function encounterQueueLength(opts = {}) {
  const context = currentContext(opts);
  return pruneStale(queue).filter((row) => encounterMatchesContext(row, context)).length;
}

export async function ensureEncounterQueue(opts = {}) {
  if (!encountersSyncEnabled()) return false;
  await probeEncountersAvailable();
  if (available === false) return false;
  const context = currentContext(opts);
  const key = contextKey(context);
  const sameHunt = contextKey({ route: lastRoute, routeType: lastRouteType, species: lastSpecies }) === key;
  adoptQueueContext(context);
  queue = pruneStale(queue).filter((row) => encounterMatchesContext(row, context));
  if (!sameHunt || queue.length === 0) persist();
  const need = Math.max(1, Number(opts.min || 1));
  if (queue.length >= need) return true;
  const requestGeneration=queueGeneration;
  // Pede só o déficit. Pedir BATCH_SIZE cheio a cada refill emitia ~1800
  // encontros/h contra ~600 abates/h — o excedente expirava no servidor.
  const deficit=sameHunt?Math.max(1,need-queue.length):Math.max(BATCH_SIZE,need);
  const requestCount=Math.min(BATCH_SIZE,Math.max(deficit,MIN_QUEUE_REFILL));
  // A transient empty RPC response used to be treated as a successful fetch.
  // The route then called createWild() with an empty queue, left the partner
  // standing forever, and never retried until the player teleported again.
  // Retry only the exact same context a couple of times; never fall back to a
  // client-authored wild because captures still require a server encounter id.
  for(let attempt=0;attempt<3;attempt+=1){
    await fetchEncounterBatch({
      route:context.route,
      routeType:context.routeType,
      species:context.species,
      count:requestCount,
      force:true,
    });
    // A teleport/hunt switch invalidates this refill as well as its response;
    // do not retry the stale species after the new context has been adopted.
    if(requestGeneration!==queueGeneration||desiredContextKey!==key)return false;
    if(queue.some(row=>encounterMatchesContext(row,context)))return true;
    if(attempt<2)await new Promise(resolve=>setTimeout(resolve,120*(attempt+1)));
  }
  return queue.some(row=>encounterMatchesContext(row,context));
}

export async function fetchEncounterBatch({
  route,
  routeType,
  species,
  count = BATCH_SIZE,
  force = false,
  zoneSpecial = false,
} = {}) {
  if (!encountersSyncEnabled()) return [];
  const context = currentContext({ route, routeType, species });
  const pin = context.species;
  const altarActive = !!activeAltarInvocation
    && activeAltarInvocation.remaining > 0
    && activeAltarInvocation.species === pin;
  const key = contextKey(context);
  const requestKey = `${key}|${zoneSpecial ? 'special' : 'route'}|${altarActive ? 'altar' : 'normal'}`;

  if (!zoneSpecial) adoptQueueContext(context);
  if (!force
    && !zoneSpecial
    && queue.length
    && desiredContextKey === key
    && queue.some((row) => encounterMatchesContext(row, context))) {
    return queue;
  }

  const existing = inFlight.get(requestKey);
  if (existing) return existing;

  const requestGeneration = queueGeneration;
  let requestPromise;
  requestPromise = (async () => {
    try {
      const payload = {
        route: context.route,
        count: Math.max(1, Math.min(60, count || BATCH_SIZE)),
        routeType: context.routeType,
        species: pin || undefined,
        zoneSpecial: !!zoneSpecial,
      };
      const response = altarActive
        ? await requestAltarEncountersRpc(payload, crypto.randomUUID())
        : await requestEncountersRpc(payload, crypto.randomUUID());

      // A troca de hunt/altar durante o await invalida a resposta antiga. Nunca
      // deixe um lote atrasado sobrescrever o contexto mais novo.
      if (!zoneSpecial && (requestGeneration !== queueGeneration || desiredContextKey !== key)) {
        return [];
      }

      if (altarActive && activeAltarInvocation) {
        const remaining = Number(response?.altar_remaining);
        if (Number.isFinite(remaining)) activeAltarInvocation.remaining = Math.max(0, remaining);
        if (activeAltarInvocation.remaining <= 0) activeAltarInvocation = null;
        hooks.onAltarProgress?.({
          species: pin,
          remaining: activeAltarInvocation?.remaining || 0,
          response,
        });
      }
      available = true;
      const rows = (response?.encounters || [])
        .map((row) => ({ ...row, state: row.state || 'issued' }))
        .filter((row) => encounterMatchesContext(row, context));
      if (zoneSpecial) {
        // Especiais não misturam com o lote de rota
        return rows;
      }
      batchId = response?.batch_id || null;
      queue = pruneStale(queue)
        .filter((row) => encounterMatchesContext(row, context))
        .concat(rows);
      lastRoute = context.route;
      lastRouteType = context.routeType;
      lastSpecies = pin;
      desiredContextKey = key;
      persist();
      return queue;
    } catch (error) {
      console.warn('request_encounters failed', error);
      const msg = String(error?.message || error?.code || '');
      if (/function|not find|PGRST|404|42883/i.test(msg)) available = false;
      throw error;
    } finally {
      if (inFlight.get(requestKey) === requestPromise) inFlight.delete(requestKey);
    }
  })();
  inFlight.set(requestKey, requestPromise);
  return requestPromise;
}

/** Consome o próximo encontro issued do lote (ou pede novo). */
export async function takeServerEncounter(opts = {}) {
  if (!encountersSyncEnabled()) return null;
  await probeEncountersAvailable();
  if (available === false) return null;

  const context = currentContext({
    route: opts.route,
    routeType: opts.routeType,
    species: opts.species !== undefined
      ? opts.species
      : (opts.huntSpecies !== undefined ? opts.huntSpecies : undefined),
  });
  adoptQueueContext(context);
  queue = pruneStale(queue).filter((row) => encounterMatchesContext(row, context));
  if (!queue.length) {
    await fetchEncounterBatch({
      route: context.route,
      routeType: context.routeType,
      species: context.species,
      force: true,
    });
  }

  queue = pruneStale(queue).filter((row) => encounterMatchesContext(row, context));
  if (!queue.length) return null;
  const next = queue.shift();
  persist();

  if (queue.length < MIN_QUEUE_REFILL) {
    fetchEncounterBatch({
      route: context.route,
      routeType: context.routeType,
      species: context.species,
      force: true,
    }).catch(() => {});
  }
  return next;
}

export function peekEncounterQueue(opts = {}) {
  const context = currentContext(opts);
  return pruneStale(queue).filter((row) => encounterMatchesContext(row, context));
}

export async function initEncounterSync({
  account,
  enabled: wantEnabled = true,
  getRouteLevel,
  getRouteType,
  getHuntSpecies,
  onAltarProgress,
} = {}) {
  userId = account?.session?.user?.id || '';
  enabled = !!wantEnabled && !!userId;
  available = null;
  queueGeneration += 1;
  inFlight.clear();
  activeAltarInvocation = null;
  hooks = {
    getRouteLevel: getRouteLevel || hooks.getRouteLevel,
    getRouteType: getRouteType || hooks.getRouteType,
    getHuntSpecies: getHuntSpecies || hooks.getHuntSpecies,
    onAltarProgress: onAltarProgress || hooks.onAltarProgress,
  };
  if (!userId) return { enabled: false, available: false };
  restore();
  await probeEncountersAvailable();
  return { enabled: encountersSyncEnabled(), available };
}
